import { Box, Button, Dialog, DialogContent, Stack, Typography } from "@mui/material";
import { useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import type { UserRole } from "../../auth/auth";
import type { PublicPackageResponse } from "../../publicPackages/publicPackagesApi";
import { ECommerceOffersInRegion } from "./ECommerceOffersInRegion";
import { ECommerceTrips } from "./ECommerceTrips";
import type { FormatType } from "./FormatSelector";
import { FormatSelector } from "./FormatSelector";
import { ImageCarousel } from "./ImageCarousel";
import { PackageActions } from "./PackageActions";
import { PackageDayDetails } from "./PackageDayDetails";
import { PackageHeader } from "./PackageHeader";
import PackageMap from "./PackageMap";
import { ParametersAndRoute } from "./ParametersAndRoute";
import { SubordinatePackages } from "./SubordinatePackages";
import { SuggestedPackages } from "./SuggestedPackages";
import { TimelineSummary } from "./TimelineSummary";

interface PackageViewProps {
  readonly value: PublicPackageResponse;
  readonly maxWidth: number;
  readonly role: UserRole | null;
}

export function PackageDetails({ onBack, value }: { onBack: () => void } & PackageViewProps) {
  const [format, setFormat] = useState<FormatType>("plan");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState("");
  const packageDetails = value;

  const handleOpenLightbox = (imageSrc: string) => {
    setLightboxImage(imageSrc);
    setLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage("");
  };

  return (
    <Box sx={{ animation: "fadeIn 0.4s ease-out", pb: 8 }}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          mb: 4,
        }}
      >
        <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ color: "text.secondary", fontWeight: "bold" }}>
          Wróć do listy pakietów
        </Button>

        {/* Akcje spójne z zamówieniami (color inherit/primary) */}
        <PackageActions forPackage={packageDetails} format={format} />
      </Stack>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "7.5fr 4.5fr",
            lg: "8.5fr 3.5fr",
          },
          gap: { xs: 3, md: 5 },
          alignItems: "stretch",
          mb: { xs: 3, md: 5 },
        }}
      >
        <ImageCarousel images={packageDetails.media.images} onClick={handleOpenLightbox} />
        <PackageMap handleOpenLightbox={handleOpenLightbox} mapImageUrl={packageDetails.media.map?.www.url} />
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "minmax(0, 7.5fr) minmax(0, 4.5fr)",
            lg: "minmax(0, 8.5fr) minmax(0, 3.5fr)",
          },
          gap: { xs: 3, md: 5 },
          alignItems: "start",
          minWidth: 0,
          width: "100%",
        }}
      >
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <SubordinatePackages forPackage={packageDetails}></SubordinatePackages>
          <PackageHeader forPackage={packageDetails} />

          <Box sx={{ mb: 4, display: "flex", alignItems: "center", gap: 3 }}>
            <FormatSelector value={format} onChange={setFormat} forPackage={packageDetails} />
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              Wybierz preferowany format prezentacji programu.
            </Typography>
          </Box>

          {/* OŚ CZASU (Wspólna dla obu widoków, różni się formatowaniem detali) */}
          <Box sx={{ position: "relative", ml: { xs: 2, md: 3 } }}>
            <Box sx={{ position: "absolute", top: 0, bottom: 0, left: 19, width: 2, bgcolor: "rgba(0,0,0,0.08)" }} />

            <Stack spacing={4}>
              {packageDetails.days.map((step, idx) => (
                <PackageDayDetails
                  key={idx}
                  index={idx + 1}
                  day={step}
                  format={format}
                  onImageClick={handleOpenLightbox}
                />
              ))}
            </Stack>
          </Box>
        </Box>

        <Box>
          <Box sx={{ position: "sticky", top: 100 }}>
            <ParametersAndRoute forPackage={packageDetails} />

            <TimelineSummary forPackage={packageDetails} />

            <ECommerceTrips forPackage={packageDetails} />

            {packageDetails.regionId && (
              <ECommerceOffersInRegion region={packageDetails.regionId} exceptPackage={packageDetails.id.id} />
            )}
          </Box>
        </Box>
      </Box>
      <SuggestedPackages forPackage={packageDetails} maxItems={4} />
      <Dialog
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        maxWidth="lg"
        fullWidth
        slotProps={{
          paper: { sx: { bgcolor: "transparent", boxShadow: "none" } },
        }}
      >
        <DialogContent
          sx={{ position: "relative", p: 0, display: "flex", justifyContent: "center", alignItems: "center" }}
        >
          <Box component="img" src={lightboxImage} sx={{ maxWidth: "100%", maxHeight: "90vh", borderRadius: 6 }} />
          <Button
            variant="contained"
            color="primary"
            startIcon={<DownloadIcon />}
            sx={{ position: "absolute", bottom: 16, right: 16, borderRadius: 6 }}
          >
            Pobierz to zdjęcie
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
